import { cache as cornerstoneCache, metaData } from "@cornerstonejs/core";
import {
  cancelLoadImage,
  loadAndCacheImage,
} from "@cornerstonejs/core/loaders/imageLoader";
import * as dicomImageLoader from "@cornerstonejs/dicom-image-loader";
import { DICOMWEB_BASE, API_BASE_URL, MULTIFRAME_PREFETCH_CONCURRENCY } from "../../../constants/Constants";
import axiosInterceptor from "../../../helpers/Interceptor";
import axios from "axios";

/**
 * Cheap "is this imageId already in Cornerstone's image cache?" check.
 * Used by the prefetch path to short-circuit cached frames so revisits
 * to an already-loaded multi-frame instance show 100% loaded
 * immediately, without re-running every frame through the throttle
 * queue.
 *
 * Cornerstone3D's cache exposes `isLoaded(imageId)` — true only once
 * the pixel data is in memory. `getImageLoadObject` would return truthy
 * for an in-flight load too, which is not what we want.
 */
export const isFrameCached = (imageId) => {
  if (typeof imageId !== "string" || !imageId) return false;
  try {
    return !!cornerstoneCache.isLoaded(imageId);
  } catch (err) {
    return false;
  }
};

// User's preferred cine FPS (the AccountAndSettings "cineFramesPerSecond"
// preference, surfaced via studyviewer context as cINEFramesPerSecond). Stored
// at module scope because resolveInitialFps is also called from scroll-driven
// helpers (setupMixedSeriesEngineForInstance) that have no parameter path to
// thread it through. The owning component pushes the current value here via
// setUserPreferredCineFps so resolveInitialFps can fall back to it whenever the
// DICOM rate tags are missing — without it the resolver dropped straight to the
// hard-coded fallback and the user's preference was ignored.
let userPreferredCineFps = null;

export const setUserPreferredCineFps = (fps) => {
  const n = Number(fps);
  userPreferredCineFps = Number.isFinite(n) && n > 0 ? n : null;
};

export const MULTI_FRAME_FPS_MIN = 1;
// Ceiling raised to 144 so high-rate acquisitions (e.g. fetal echo at 60–100+
// fps) play at their tagged source rate instead of being capped at 60. Note
// the engine still advances frames by wall-clock time, so actual on-screen
// rate is bounded by the display refresh / device throughput regardless.
export const MULTI_FRAME_FPS_MAX = 144;
export const MULTI_FRAME_FPS_FALLBACK = 10;

const clampFps = (fps) => {
  if (!Number.isFinite(fps) || fps <= 0) return null;
  if (fps < MULTI_FRAME_FPS_MIN) return MULTI_FRAME_FPS_MIN;
  if (fps > MULTI_FRAME_FPS_MAX) return MULTI_FRAME_FPS_MAX;
  return fps;
};

export const readMultiFrameMetadata = (imageId) => {
  if (!imageId) return null;
  const mf = metaData.get("multiframeModule", imageId);
  if (!mf || typeof mf.NumberOfFrames !== "number") return null;
  return mf;
};

export const isMultiFrameImageId = (imageId) => {
  const mf = readMultiFrameMetadata(imageId);
  return !!mf && mf.NumberOfFrames > 1;
};

// Per-instance metadata populated at study load — keyed by the wadouri:
// imageId that the viewer hands around. Lets detectMultiFrame skip the
// full-file Cornerstone parse for instances whose NumberOfFrames came from
// the backend's study API.
//
// Shape: { studyId, studyUid, seriesUid, sopUid, numberOfFrames,
//          preferWadouriFrames }
// `studyId` is the backend's internal numeric study id — woven into the
// WADO-RS URL as a `/sid/{studyId}` path segment before `/studies/...`
// so it ends up in the imageURI without disrupting the `/frames/N`
// suffix the wadors loader relies on for multi-frame metadata lookup.
//
// `preferWadouriFrames` marks instances whose multi-frame expansion must
// stay on the wadouri `?frame=N` path and NOT rebuild the wadors per-frame
// path from the UIDs above. Set for priors-loaded studies: their per-frame
// WADO-RS fetches (jwtServices/dicomweb) aren't authorized under the priors
// session JWT, so a wadors rebuild 401s the /metadata fetch and leaves the
// wadors loader without pixel metadata — surfacing at image construction as
// "Cannot read properties of undefined (reading 'samplesPerPixel')".
const instanceMetadataByImageId = new Map();

export const registerImageMetadata = (imageId, metadata) => {
  if (typeof imageId !== "string" || !imageId || !metadata) return;
  // Defensive copy so the caller can mutate its own object freely.
  instanceMetadataByImageId.set(imageId, {
    studyId: metadata.studyId != null ? metadata.studyId : null,
    studyUid: metadata.studyUid || null,
    seriesUid: metadata.seriesUid || null,
    sopUid: metadata.sopUid || null,
    numberOfFrames:
      typeof metadata.numberOfFrames === "number"
        ? metadata.numberOfFrames
        : null,
    preferWadouriFrames: metadata.preferWadouriFrames === true,
  });
};

export const getImageMetadata = (imageId) => {
  if (typeof imageId !== "string") return null;
  return instanceMetadataByImageId.get(imageId) || null;
};

export const clearImageMetadata = () => {
  instanceMetadataByImageId.clear();
};

export const detectMultiFrame = async (imageId) => {
  if (!imageId) return { isMultiFrame: false, numberOfFrames: 0 };

  // Single source of truth: the registry populated at study load from the
  // backend's `numberOfFrames` field.
  //   - registered.numberOfFrames > 1 → multi-frame (use wadors path)
  //   - registered.numberOfFrames === 1 OR null/undefined → single-frame
  //     (use the existing wadouri path; never parse the DICOM here)
  //
  // NULL on legacy rows is treated identically to 1: a single-frame
  // instance. The old fallback that called `loadAndCacheImage(imageId)`
  // forced a full-file download every time the registry was missing the
  // count, which is exactly the regression we're trying to eliminate.
  const registered = getImageMetadata(imageId);
  const n = registered && typeof registered.numberOfFrames === "number"
    ? registered.numberOfFrames
    : null;
  if (n !== null && n > 1) {
    return {
      isMultiFrame: true,
      numberOfFrames: n,
      metadata: null,
      source: "registry",
    };
  }
  return {
    isMultiFrame: false,
    numberOfFrames: n === null ? 0 : n,
    metadata: null,
    source: "registry",
  };
};

const appendFrameQuery = (imageId, frame) => {
  // Cornerstone's wadouri loader treats `?frame=N` as a multi-frame extraction
  // hint. The underlying DICOM is parsed once and cached; every frame request
  // against the same base imageId reuses that parsed dataset, so we get
  // per-frame ImageBitmaps without N HTTP fetches.
  if (imageId.includes("?")) {
    return `${imageId}&frame=${frame}`;
  }
  return `${imageId}?frame=${frame}`;
};

export const buildFrameImageIds = (baseImageId, numberOfFrames) => {
  if (
    !baseImageId ||
    !Number.isInteger(numberOfFrames) ||
    numberOfFrames < 2
  ) {
    return baseImageId ? [baseImageId] : [];
  }
  const ids = new Array(numberOfFrames);
  for (let i = 0; i < numberOfFrames; i++) {
    // DICOM frame numbers are 1-indexed.
    ids[i] = appendFrameQuery(baseImageId, i + 1);
  }
  return ids;
};

// Resolves the imageId that should ACTUALLY be loaded / cache-checked for a
// stack's base imageId. For an un-split multi-frame instance the viewport and
// prefetch render per-frame URLs (`?frame=N`), never the bare base — and the
// wadouri loader can HANG on that frame-less base URL, wedging the series
// preload. So for multi-frame instances we return the frame-1 URL (the same id
// the viewport loads); for everything else, the id itself. Wadors frame stacks
// already end in `/frames/N`, so they're returned unchanged.
export const resolvePrimaryFrameImageId = (baseImageId) => {
  if (typeof baseImageId !== "string" || !baseImageId) return baseImageId;
  const meta = getImageMetadata(baseImageId);
  const isMultiFrame =
    meta && typeof meta.numberOfFrames === "number" && meta.numberOfFrames > 1;
  if (!isMultiFrame) return baseImageId;
  if (baseImageId.startsWith("wadors:")) return baseImageId;
  return appendFrameQuery(baseImageId, 1);
};

// True if a stack's base imageId is effectively loaded in the cornerstone cache
// — checks the primary frame URL for multi-frame instances (what actually gets
// cached) as well as the bare id, so series-preload progress can reach 100%.
export const isInstanceCached = (baseImageId) =>
  isFrameCached(resolvePrimaryFrameImageId(baseImageId)) ||
  isFrameCached(baseImageId);

// --- wadors per-frame integration ----------------------------------------
//
// For un-split multi-frame DICOMs (CT, Enhanced CT, firm-opt-out, legacy
// data), we serve each frame from the backend WADO-RS frame endpoint
// instead of letting Cornerstone download the full file once and split
// client-side.
//
// Cornerstone's `dicomImageLoader.init()` already registers the wadors
// metadata provider (see registerLoaders.js in @cornerstonejs/dicom-image-
// loader). That provider reads from `wadors.metaDataManager` and exposes
// module-style metadata (imagePixelModule, generalSeriesModule, etc.) to
// Cornerstone. We feed metaDataManager via `/metadata` (see
// fetchAndRegisterMultiFrameMetadata below) — no custom bridge needed.
const stripScheme = (imageId) => {
  if (typeof imageId !== "string") return imageId;
  const colonIdx = imageId.indexOf(":");
  return colonIdx === -1 ? imageId : imageId.slice(colonIdx + 1);
};

// Resolves study/series/SOP UIDs for an imageId. Preferred source is the
// registry populated at study load (no DICOM parse needed). Falls back to
// the wadouri-cached parsed dataset for legacy instances whose metadata
// hasn't been registered.
//
// The registry path also carries the backend's internal numeric `studyId`
// (used as the per-study cache-directory key in the WADO-RS endpoint).
// The wadouri fallback path can't surface it — callers must tolerate
// `studyId` being undefined there.
export const extractInstanceUids = (baseImageId) => {
  const registered = getImageMetadata(baseImageId);
  if (registered && registered.studyUid && registered.seriesUid && registered.sopUid) {
    return {
      studyId: registered.studyId != null ? registered.studyId : undefined,
      studyUid: registered.studyUid,
      seriesUid: registered.seriesUid,
      sopUid: registered.sopUid,
    };
  }
  try {
    const uri = stripScheme(baseImageId);
    const cache = dicomImageLoader?.wadouri?.dataSetCacheManager;
    if (!cache || typeof cache.get !== "function") return null;
    const ds = cache.get(uri);
    if (!ds || typeof ds.string !== "function") return null;
    const studyUid = ds.string("x0020000d");
    const seriesUid = ds.string("x0020000e");
    const sopUid = ds.string("x00080018");
    if (!studyUid || !seriesUid || !sopUid) return null;
    return { studyUid, seriesUid, sopUid };
  } catch (err) {
    console.debug("[MultiFrame] extractInstanceUids failed:", err);
    return null;
  }
};

// Builds the studyId path prefix that goes BEFORE /studies/... in every
// WADO-RS URL. studyId must be in the PATH (not a query parameter) for
// the frame URLs because Cornerstone's dicom-image-loader caches
// multi-frame metadata keyed by the imageURI ending in `/frames/N`. If
// `?studyId=…` appears AFTER `/frames/N` the frame-1 lookup slices it
// off and misses the registered key — surfacing as
// "Cannot read properties of undefined (reading 'samplesPerPixel')"
// during image construction. We keep the prefix on the metadata /
// frameIndex routes too for path symmetry.
const studyIdPathPrefix = (studyId) => {
  if (studyId == null) return "";
  return `/sid/${encodeURIComponent(studyId)}`;
};

// Builds N wadors imageIds for the un-split multi-frame instance and
// registers each one in the wadors->wadouri bridge so Cornerstone's metadata
// providers continue to answer correctly. Returns null if any of the inputs
// are missing — caller must fall back.
export const buildWadorsFrameImageIds = ({
  baseImageId,
  studyId,
  studyUid,
  seriesUid,
  sopUid,
  numberOfFrames,
}) => {
  if (!baseImageId || !Number.isInteger(numberOfFrames) || numberOfFrames < 1) {
    return null;
  }
  // If baseImageId is already a wadors frame URL (ends in /frames/N), derive
  // per-frame URLs by replacing the frame number. This handles both the JWT
  // path (wadors:.../jwtServices/dicomweb/...) and the share-token path
  // (wadors:.../sharedServices/dicomweb/study/{token}/...) without needing
  // a separate code path for each.
  const frameMatch = baseImageId.match(/^(wadors:.+\/frames)\/\d+$/);
  if (frameMatch) {
    const frameBase = frameMatch[1];
    const ids = new Array(numberOfFrames);
    for (let i = 0; i < numberOfFrames; i++) ids[i] = `${frameBase}/${i + 1}`;
    return ids;
  }
  // Fallback: build per-frame URLs from UIDs + JWT DICOMWEB_BASE.
  if (!studyUid || !seriesUid || !sopUid) return null;
  const base = `${DICOMWEB_BASE}${studyIdPathPrefix(studyId)}`
    + `/studies/${encodeURIComponent(studyUid)}`
    + `/series/${encodeURIComponent(seriesUid)}`
    + `/instances/${encodeURIComponent(sopUid)}/frames`;
  const ids = new Array(numberOfFrames);
  for (let i = 0; i < numberOfFrames; i++) {
    ids[i] = `wadors:${base}/${i + 1}`;
  }
  return ids;
};

// Kept as a no-op for the existing callers (viewport teardown still calls
// it). Previously this cleaned up the wadors→wadouri bridge map; the bridge
// is gone now (wadors metadata flows through dicomImageLoader's built-in
// provider via metaDataManager).
export const releaseWadorsFrameImageIds = () => {};

// --- /metadata fetch + wadors metaDataManager registration ---------------
//
// Cornerstone's wadors loader needs DICOMweb naturalized metadata
// (Rows/Cols/Bits/Photometric/etc.) to decode the frame bytes it fetches.
// We register metadata under the imageId that ends in `/frames/1` per
// metaDataManager's convention — subsequent frames look up frame-1's
// metadata and apply per-frame transforms via combineFrameInstance.

// Avoid duplicate /metadata fetches when a study load registers the same
// SOP UID more than once (priors carrying the same Image, multiple stacks
// pointing at the same DICOM, etc.). Keyed by SOP UID.
const inflightMetadataFetches = new Map();
const registeredMetadataSops = new Set();

const buildWadorsFrameUrl = ({ studyId, studyUid, seriesUid, sopUid, frameNumber }) =>
  `wadors:${DICOMWEB_BASE}${studyIdPathPrefix(studyId)}`
    + `/studies/${encodeURIComponent(studyUid)}`
    + `/series/${encodeURIComponent(seriesUid)}`
    + `/instances/${encodeURIComponent(sopUid)}/frames/${frameNumber}`;

// Wadors imageId for frame 1 of the given instance. This is the canonical
// id under which metaDataManager.add() stores the dataset — every other
// frame imageId for the same instance falls back to this entry.
export const buildWadorsFrame1ImageId = ({ studyId, studyUid, seriesUid, sopUid }) =>
  buildWadorsFrameUrl({ studyId, studyUid, seriesUid, sopUid, frameNumber: 1 });

// Share-token-gated wadors frame 1 imageId. Routes through
// /sharedServices/dicomweb/ which is permit-all in Spring Security and
// validates the share token embedded in the path.
export const buildSharedWadorsFrame1ImageId = ({ shareToken, studyUid, seriesUid, sopUid }) =>
  `wadors:${API_BASE_URL}sharedServices/dicomweb/study/${encodeURIComponent(shareToken)}`
    + `/studies/${encodeURIComponent(studyUid)}`
    + `/series/${encodeURIComponent(seriesUid)}`
    + `/instances/${encodeURIComponent(sopUid)}/frames/1`;

/**
 * Fetches /metadata for one instance, registers it with the wadors loader,
 * and (best-effort) kicks off a background prefetch of frames 2..N so the
 * cine is ready by the time the user clicks the series.
 *
 * Idempotent per SOP UID — second call returns the original promise so
 * callers can await without worrying about duplication.
 *
 * Returns the wadors imageId that the registered metadata is keyed under,
 * or null if any of the inputs are missing.
 */
export const fetchAndRegisterMultiFrameMetadata = ({
  studyId,
  studyUid,
  seriesUid,
  sopUid,
  numberOfFrames,
  // Optional: when the caller already has the canonical frame-1 imageId
  // (e.g. a share-token wadors URL), pass it here so the metadata URL is
  // derived from it instead of being rebuilt from UIDs + JWT base.
  frame1ImageId: providedFrame1ImageId,
}) => {
  if (!studyUid || !seriesUid || !sopUid) return Promise.resolve(null);
  if (registeredMetadataSops.has(sopUid)) {
    return Promise.resolve(
      providedFrame1ImageId || buildWadorsFrame1ImageId({ studyId, studyUid, seriesUid, sopUid })
    );
  }
  const existing = inflightMetadataFetches.get(sopUid);
  if (existing) return existing;

  const frame1ImageId = providedFrame1ImageId
    || buildWadorsFrame1ImageId({ studyId, studyUid, seriesUid, sopUid });

  // Determine the /metadata URL and auth strategy.
  // Share-token path  (wadors:…/sharedServices/dicomweb/…): derive the
  // metadata URL by stripping `wadors:` and replacing `/frames/1` →
  // `/metadata`, then fetch without credentials (permit-all endpoint).
  // JWT path: use the axiosInterceptor with credentials as before.
  let metadataUrl;
  let isShared = false;
  if (frame1ImageId.includes('/sharedServices/dicomweb/')) {
    isShared = true;
    metadataUrl = frame1ImageId.slice('wadors:'.length).replace(/\/frames\/\d+$/, '/metadata');
  } else {
    metadataUrl =
      `jwtServices/dicomweb${studyIdPathPrefix(studyId)}`
      + `/studies/${encodeURIComponent(studyUid)}`
      + `/series/${encodeURIComponent(seriesUid)}`
      + `/instances/${encodeURIComponent(sopUid)}/metadata`;
  }

  const request = isShared
    ? axios.get(metadataUrl)
    : axiosInterceptor.get(metadataUrl, { withCredentials: true });

  const promise = request
    .then((res) => {
      const body = res?.data;
      // /metadata returns a 1-element array per DICOMweb convention.
      const instance = Array.isArray(body) ? body[0] : body;
      if (!instance) {
        throw new Error("empty metadata response");
      }
      try {
        dicomImageLoader.wadors.metaDataManager.add(frame1ImageId, instance);
      } catch (err) {
        console.debug(
          "[MultiFrame] wadors.metaDataManager.add failed for",
          frame1ImageId,
          err
        );
        throw err;
      }
      registeredMetadataSops.add(sopUid);
      return frame1ImageId;
    })
    .catch((err) => {
      console.debug("[MultiFrame] /metadata fetch failed sop=" + sopUid, err);
      return null;
    })
    .finally(() => {
      inflightMetadataFetches.delete(sopUid);
    });
  inflightMetadataFetches.set(sopUid, promise);
  return promise;
};

// ---- Prefetch dispatch --------------------------------------------------
//
// Cornerstone3D already has a built-in request pool (imageLoadPoolManager)
// with dedicated lanes per request type:
//   - Interaction: 6 concurrent slots — for the frame the user is looking
//                  at right now
//   - Prefetch:    5 concurrent slots — for background warmup
//   - Thumbnail:   6 concurrent slots — for thumbnail panel
// Within each lane, requests are sorted by their numeric `priority`
// (LOWER number = HIGHER priority — `0` is the topmost).
//
// We used to put our own throttle queue in front of this, which was
// counter-productive: every `loadAndCacheImage` call already hits the
// Cornerstone pool, so our extra layer just delayed dispatch without
// adding any actual rate limiting. Worse, when our throttle held back a
// frame's load, the engine ticked past it and Cornerstone re-fetched
// because the cache entry hadn't been created yet.
//
// New design: call `loadAndCacheImage` directly, threading our friendly
// "high/normal/low" priority labels through to Cornerstone's numeric
// priority. We still dedupe by imageId (so multiple call sites for the
// same frame return the same promise) and we still resolve on failure
// (not reject) so callers can rely on the promise to fire exactly once.

const PREFETCH_CORNERSTONE_PRIORITY = {
  high: 0,    // active multi-frame instance the user is looking at
  normal: 5,  // thumbnail-panel warmup, sibling multi-frames once
              // active is in flight
  low: 10,    // reserved
};

const prefetchInflight = new Map(); // imageId -> Promise (returned to callers)

// --- Client-side prefetch semaphore --------------------------------------
//
// enqueueFramePrefetch -> loadAndCacheImage bypasses Cornerstone's
// imageLoadPoolManager and hits the wadors loader (xhr.send()) directly, so
// without this gate a 776-frame cine fires 776 XHRs in one tick. The semaphore
// caps concurrent in-flight loads at MULTIFRAME_PREFETCH_CONCURRENCY; each
// completion releases a slot and pulls the next waiter, highest priority first.
//
// Numeric priority mirrors PREFETCH_CORNERSTONE_PRIORITY (LOWER = more urgent):
// a "high" frame requested during scrub jumps ahead of already-queued "low"
// background frames instead of waiting behind them (strict FIFO would stall the
// frame the user is actually looking at).
let prefetchActiveCount = 0;
const prefetchWaiters = []; // { priority, run } — run() starts the actual load

// Pull and start the highest-priority waiter(s) while slots are free.
const pumpPrefetchQueue = () => {
  while (
    prefetchActiveCount < MULTIFRAME_PREFETCH_CONCURRENCY &&
    prefetchWaiters.length > 0
  ) {
    // Lowest numeric priority = most urgent. Stable within a priority band by
    // scanning for the first minimum (insertion order preserved).
    let bestIdx = 0;
    for (let i = 1; i < prefetchWaiters.length; i++) {
      if (prefetchWaiters[i].priority < prefetchWaiters[bestIdx].priority) {
        bestIdx = i;
      }
    }
    const waiter = prefetchWaiters.splice(bestIdx, 1)[0];
    prefetchActiveCount++;
    waiter.run();
  }
};

/**
 * Dedupe + throttled dispatch of a frame load.
 * Returns a Promise that resolves (with the image, or null on failure)
 * once the load settles. Calling twice for the same imageId returns
 * the same promise — no duplicate XHR. The load itself is gated by the
 * prefetch semaphore so at most MULTIFRAME_PREFETCH_CONCURRENCY run at once.
 *
 * @param imageId
 * @param opts.priority  "high" | "normal" | "low" (default "normal")
 */
export const enqueueFramePrefetch = (imageId, opts) => {
  if (typeof imageId !== "string" || !imageId) return Promise.resolve(null);
  const existing = prefetchInflight.get(imageId);
  if (existing) return existing;

  const priorityLabel =
    opts && (opts.priority === "high" || opts.priority === "low")
      ? opts.priority
      : "normal";
  const cornerstonePriority = PREFETCH_CORNERSTONE_PRIORITY[priorityLabel];

  // Promise handed back to callers now; the actual loadAndCacheImage is deferred
  // until a semaphore slot frees up (run() below).
  let resolveOuter;
  const promise = new Promise((resolve) => {
    resolveOuter = resolve;
  });
  prefetchInflight.set(imageId, promise);

  const run = () => {
    loadAndCacheImage(imageId, {
      priority: cornerstonePriority,
      requestType: "prefetch",
    })
      .then(
        (image) => image,
        (err) => {
          // Resolve null on failure — callers don't need the rejection, and not
          // surfacing it means an unhandled-promise-rejection warning doesn't
          // fire for every flaky frame.
          console.debug("[MultiFrame] prefetch failed", imageId, err);
          return null;
        }
      )
      .then((result) => {
        // Release the slot BEFORE resolving so the next waiter starts promptly,
        // and clean up the dedup entry so a later revisit can re-enqueue (if the
        // cache evicted in the meantime).
        prefetchActiveCount--;
        if (prefetchInflight.get(imageId) === promise) {
          prefetchInflight.delete(imageId);
        }
        pumpPrefetchQueue();
        resolveOuter(result);
      });
  };

  prefetchWaiters.push({ priority: cornerstonePriority, run });
  pumpPrefetchQueue();
  return promise;
};

// Number of frames (starting from frame 2) that are prefetched at HIGH priority
// on viewport open.  With bucket-direct serving each range GET is ~50–150 ms, so
// 50 frames warm in 1–3 seconds — enough for a smooth first cine loop.
export const INITIAL_PREFETCH_WINDOW = 50;

// Sliding window sizes for long cines (> INITIAL_PREFETCH_WINDOW frames).
// Chosen to cover one second of 30-fps playback in each direction.
export const PREFETCH_LOOK_AHEAD = 30;
export const PREFETCH_LOOK_BEHIND = 5;

/**
 * Fire-and-forget background prefetch of frames 2..numberOfFrames for a
 * multi-frame instance.  Frame 1 is loaded by setStack and not re-fetched.
 *
 * For the first {@link INITIAL_PREFETCH_WINDOW} frames HIGH priority is used
 * so cine starts quickly.  Remaining frames use NORMAL priority.
 */
export const prefetchAdditionalFrames = ({
  studyId,
  studyUid,
  seriesUid,
  sopUid,
  numberOfFrames,
}) => {
  if (
    !studyUid ||
    !seriesUid ||
    !sopUid ||
    !Number.isInteger(numberOfFrames) ||
    numberOfFrames < 2
  ) {
    return;
  }
  for (let i = 2; i <= numberOfFrames; i++) {
    const id = buildWadorsFrameUrl({
      studyId, studyUid, seriesUid, sopUid, frameNumber: i,
    });
    // First INITIAL_PREFETCH_WINDOW frames at HIGH priority for fast first-loop cine.
    const priority = i <= INITIAL_PREFETCH_WINDOW + 1 ? "high" : "normal";
    enqueueFramePrefetch(id, { priority });
  }
};

/**
 * Slide the high-priority prefetch window to [{@code currentFrame}-{@link PREFETCH_LOOK_BEHIND},
 * {@code currentFrame}+{@link PREFETCH_LOOK_AHEAD}].  Called by the playback engine on each
 * frame advance so frames ahead of the playback cursor are always warm.
 *
 * Only frames that are not already in the Cornerstone cache are enqueued —
 * calling this every rAF tick (60 fps) is safe; deduplication is handled
 * by {@link enqueueFramePrefetch}.
 *
 * @param {object} opts
 * @param {number} opts.currentFrame  1-indexed frame number (matches WADO-RS)
 */
export const advancePrefetchWindow = ({
  studyId,
  studyUid,
  seriesUid,
  sopUid,
  numberOfFrames,
  currentFrame,
  lookAhead = PREFETCH_LOOK_AHEAD,
  lookBehind = PREFETCH_LOOK_BEHIND,
}) => {
  if (!studyUid || !seriesUid || !sopUid || !Number.isInteger(numberOfFrames)) return;
  const start = Math.max(1, currentFrame - lookBehind);
  const end   = Math.min(numberOfFrames, currentFrame + lookAhead);
  for (let f = start; f <= end; f++) {
    const id = buildWadorsFrameUrl({ studyId, studyUid, seriesUid, sopUid, frameNumber: f });
    if (!isFrameCached(id)) {
      enqueueFramePrefetch(id, { priority: "high" });
    }
  }
};

// Reset hook — useful when navigating between studies so stale metadata
// for previously-open instances doesn't leak.
export const clearMultiFrameMetadataRegistry = () => {
  registeredMetadataSops.clear();
  inflightMetadataFetches.clear();
};

// DICOMweb JSON value extractor — tags are { vr, Value: [...] }. Returns the
// first value as a finite number (DS/IS come back as strings), or null.
const dicomWebFirstNumber = (element) => {
  const v = element && Array.isArray(element.Value) ? element.Value[0] : undefined;
  if (v == null) return null;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

// Reads the cine frame rate straight from the registered wadors /metadata
// instance. The wadors metadata provider does NOT implement a "cineModule"
// (only wadouri's full-file parser does), so for un-split multi-frame
// instances (mobile, Enhanced CT/US, etc.) metaData.get("cineModule") is
// always empty and the source frame rate would be lost. We read the raw
// DICOMweb cine tags here instead, in the same priority order. Returns an
// fps number, or null when no rate tag is present / metadata isn't registered.
const readWadorsCineFps = (imageId) => {
  try {
    const mgr = dicomImageLoader?.wadors?.metaDataManager;
    const inst = mgr && typeof mgr.get === "function" ? mgr.get(imageId) : null;
    if (!inst) return null;
    // Priority (modern-viewer convention):
    //   1. (0008,2144) RecommendedDisplayFrameRate — the modern standard,
    //      the ideal viewing FPS requested by the acquisition device.
    //   2. (0018,0040) CineRate — legacy FPS tag; RDFR above overrides it.
    //   3. (0018,1063) FrameTime — per-frame duration (ms) → 1000 / FrameTime.
    //   4. (0018,1065) FrameTimeVector — per-frame durations → 1000 / mean.
    const rdfr = dicomWebFirstNumber(inst["00082144"]); // RecommendedDisplayFrameRate
    if (rdfr && rdfr > 0) return rdfr;
    const cineRate = dicomWebFirstNumber(inst["00180040"]); // CineRate (fps)
    if (cineRate && cineRate > 0) return cineRate;
    const frameTime = dicomWebFirstNumber(inst["00181063"]); // FrameTime (ms)
    if (frameTime && frameTime > 0) return 1000 / frameTime;
    const ftv = inst["00181065"]; // FrameTimeVector (ms, multi-valued)
    if (ftv && Array.isArray(ftv.Value) && ftv.Value.length) {
      const nums = ftv.Value.map(Number).filter((n) => Number.isFinite(n));
      if (nums.length) {
        const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
        if (mean > 0) return 1000 / mean;
      }
    }
  } catch (err) {
    console.debug("[MultiFrame] readWadorsCineFps failed:", err);
  }
  return null;
};

export const resolveInitialFps = (imageId, userPreferredFps) => {
  // Priority order (modern-viewer convention):
  //   1. DICOM per-instance tags, in this order:
  //      - (0008,2144) RecommendedDisplayFrameRate -> fps directly. The
  //        modern standard: the ideal viewing FPS the acquisition device asked
  //        for. Preferred over the legacy CineRate.
  //      - (0018,0040) CineRate                    -> fps directly (legacy).
  //      - (0018,1063) FrameTime                   -> fps = 1000 / FrameTime.
  //      - (0018,1065) FrameTimeVector             -> 1000 / mean.
  //   2. User preference (cineFramesPerSecond)
  //   3. Hard-coded fallback (10 fps)
  // Clamp to [MULTI_FRAME_FPS_MIN, MULTI_FRAME_FPS_MAX] (1–144); reject
  // NaN/0/negative silently.
  const cine = metaData.get("cineModule", imageId) || {};
  const mf = readMultiFrameMetadata(imageId) || {};

  const candidates = [];
  if (typeof mf.RecommendedDisplayFrameRate === "number") {
    candidates.push(mf.RecommendedDisplayFrameRate);
  }
  if (typeof cine.cineRate === "number") {
    candidates.push(cine.cineRate);
  }
  if (typeof cine.frameTime === "number" && cine.frameTime > 0) {
    candidates.push(1000 / cine.frameTime);
  }
  if (Array.isArray(cine.frameTimeVector) && cine.frameTimeVector.length > 0) {
    const mean =
      cine.frameTimeVector.reduce((a, b) => a + b, 0) /
      cine.frameTimeVector.length;
    if (mean > 0) candidates.push(1000 / mean);
  }

  // Wadors per-frame instances expose no cineModule, so the candidates above
  // are empty for them — read the source frame rate from the raw /metadata
  // cine tags. (Already in priority order; only used if the cineModule path
  // found nothing, since the loop returns the first valid candidate.)
  const wadorsFps = readWadorsCineFps(imageId);
  if (wadorsFps != null) {
    candidates.push(wadorsFps);
  }

  for (const candidate of candidates) {
    const fps = clampFps(candidate);
    if (fps != null) return fps;
  }

  // Explicit arg wins; otherwise fall back to the module-level user preference
  // pushed by the viewer. Either path covers "DICOM tags missing → honour the
  // user's chosen cine FPS" before the hard-coded fallback.
  const userFps = clampFps(
    Number(userPreferredFps != null ? userPreferredFps : userPreferredCineFps)
  );
  if (userFps != null) return userFps;

  return MULTI_FRAME_FPS_FALLBACK;
};
